import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { Order } from '../../types';
import './AdminDashboard.css';

interface DashboardData {
  totalOrders: number;
  totalRevenue: number;
  totalUsers: number;
  totalProducts: number;
  pendingOrders: number;
  processingOrders: number;
  recentOrders: Order[];
}

const AdminDashboard: React.FC = () => {
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async (): Promise<void> => {
    try {
      const response = await api.get<DashboardData>('/admin/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  if (!dashboard) {
    return <div className="error">Failed to load dashboard</div>;
  }

  return (
    <div className="admin-dashboard">
      <div className="container">
        <h1 className="page-title">Admin Dashboard</h1>

        <div className="dashboard-stats">
          <div className="stat-card">
            <div className="stat-icon"><i className="fas fa-box"></i></div>
            <div className="stat-content">
              <h3>{dashboard.totalOrders}</h3>
              <p>Total Orders</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><i className="fas fa-dollar-sign"></i></div>
            <div className="stat-content">
              <h3>${dashboard.totalRevenue.toFixed(2)}</h3>
              <p>Total Revenue</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><i className="fas fa-users"></i></div>
            <div className="stat-content">
              <h3>{dashboard.totalUsers}</h3>
              <p>Total Users</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><i className="fas fa-image"></i></div>
            <div className="stat-content">
              <h3>{dashboard.totalProducts}</h3>
              <p>Total Products</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><i className="fas fa-clock"></i></div>
            <div className="stat-content">
              <h3>{dashboard.pendingOrders}</h3>
              <p>Pending Orders</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><i className="fas fa-cog"></i></div>
            <div className="stat-content">
              <h3>{dashboard.processingOrders}</h3>
              <p>Processing Orders</p>
            </div>
          </div>
        </div>

        <div className="dashboard-sections">
          <div className="dashboard-section">
            <div className="section-header">
              <h2>Quick Actions</h2>
            </div>
            <div className="quick-actions">
              <Link to="/admin/orders" className="action-card">
                <span className="action-icon"><i className="fas fa-clipboard-list"></i></span>
                <span>Manage Orders</span>
              </Link>
              <Link to="/admin/users" className="action-card">
                <span className="action-icon"><i className="fas fa-users"></i></span>
                <span>Manage Users</span>
              </Link>
              <Link to="/admin/artworks" className="action-card">
                <span className="action-icon"><i className="fas fa-palette"></i></span>
                <span>Manage Artworks</span>
              </Link>
              <Link to="/admin/sizes" className="action-card">
                <i className="fas fa-ruler-combined"></i>
                <h3>Manage Sizes</h3>
                <p>Define sizes with base pricing</p>
              </Link>
              <Link to="/admin/material-pricing" className="action-card">
                <i className="fas fa-layer-group"></i>
                <h3>Material Pricing</h3>
                <p>Roll cost, waste, and markup</p>
              </Link>
              <Link to="/admin/artists" className="action-card">
                <i className="fas fa-user-paint-brush"></i>
                <h3>Manage Artists</h3>
                <p>Discounts, limits, and payouts</p>
              </Link>
              <Link to="/admin/artist-applications" className="action-card">
                <i className="fas fa-file-signature"></i>
                <h3>Artist Applications</h3>
                <p>Review and approve artists</p>
              </Link>
              <Link to="/admin/metrics" className="action-card">
                <i className="fas fa-chart-line"></i>
                <h3>Metrics</h3>
                <p>Views, carts, and orders</p>
              </Link>
              <Link to="/admin/settings" className="action-card">
                <i className="fas fa-sliders-h"></i>
                <h3>Settings</h3>
                <p>Artist highlight limits</p>
              </Link>
              <Link to="/admin/aspect-ratios" className="action-card">
                <span className="action-icon"><i className="fas fa-ruler-combined"></i></span>
                <span>Aspect Ratio Pricing</span>
              </Link>
              <Link to="/admin/tags" className="action-card">
                <span className="action-icon"><i className="fas fa-tags"></i></span>
                <span>Manage Tags</span>
              </Link>
              <Link to="/admin/shipping" className="action-card">
                <span className="action-icon"><i className="fas fa-truck"></i></span>
                <span>Shipping Options</span>
              </Link>
              <Link to="/admin/promotions" className="action-card">
                <span className="action-icon"><i className="fas fa-tags"></i></span>
                <span>Promotions</span>
                <small>Coupons & Sales</small>
              </Link>
              <Link to="/admin/returns" className="action-card">
                <span className="action-icon"><i className="fas fa-undo"></i></span>
                <span>Manage Returns</span>
              </Link>
              <Link to="/admin/stretcher-bars" className="action-card">
                <span className="action-icon"><i className="fas fa-ruler"></i></span>
                <span>Bar Inventory</span>
              </Link>
            </div>
          </div>

          <div className="dashboard-section">
            <div className="section-header">
              <h2>Recent Orders</h2>
              <Link to="/admin/orders" className="view-all">View All</Link>
            </div>
            <div className="recent-orders">
              {dashboard.recentOrders.length === 0 ? (
                <p>No recent orders</p>
              ) : (
                <table className="orders-table">
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Customer</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.recentOrders.map((order) => (
                      <tr key={order.id}>
                        <td>#{order.id}</td>
                        <td>{order.guestEmail || 'N/A'}</td>
                        <td>${order.totalAmount.toFixed(2)}</td>
                        <td>
                          <span className={`status-badge status-${order.status.toLowerCase()}`}>
                            {order.status}
                          </span>
                        </td>
                        <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
